/**
 * Shared offeror (company) identity for exports.
 *
 * Single source of truth for "who is submitting this proposal", consumed by
 * BOTH the PDF (React) and Word (docx) exporters so the cover page, running
 * header, and footer all show the same details.
 *
 * Reads the RAW `profiles` DB row (snake_case) first, then a camelCase
 * `CompanyCore`, then falls back to the generated cover-letter's `companyInfo`.
 * Missing federal identifiers (UEI/CAGE) surface as "[to be completed]" rather
 * than being invented — matching the submission spec.
 */

import { decodeHtmlEntities, firstReal } from './textCleanup';

export const TO_BE_COMPLETED = '[to be completed]';

export interface ExportCompanyIdentity {
  companyName: string;        // '' if unknown
  logoUrl: string | null;     // company_logo_url or null
  addressLines: string[];     // pre-formatted display lines
  phone: string;
  email: string;
  website: string;            // display form (no protocol)
  uei: string;                // '' if unknown (render TO_BE_COMPLETED at the call site)
  cage: string;
  duns: string;
  pocName: string;            // primary point of contact (signer)
  pocTitle: string;
  hasIdentity: boolean;
}

function s(v: unknown): string {
  if (v == null) return '';
  return decodeHtmlEntities(String(v)).trim();
}

function displayWebsite(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/** Format a business-address object (snake or camel) into display lines. */
export function formatAddress(addr: any): string[] {
  if (!addr) return [];
  if (typeof addr === 'string') return addr.split('\n').map((l) => l.trim()).filter(Boolean);

  const street1 = s(addr.street1 ?? addr.street ?? addr.address);
  const street2 = s(addr.street2 ?? addr.suite);
  const city = s(addr.city);
  const state = s(addr.state ?? addr.stateCode);
  const zip = s(addr.zip ?? addr.postalCode);
  const countryRaw = s(addr.country);

  const lines: string[] = [];
  if (street1) lines.push(street1);
  if (street2) lines.push(street2);

  const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) lines.push(cityStateZip);

  if (countryRaw && !/^(usa|united states( of america)?)$/i.test(countryRaw)) {
    lines.push(countryRaw);
  }
  return lines;
}

/**
 * Build the export identity from the user profile, with cover-letter fallback.
 * @param profile the raw `profiles` row (snake_case) or camelCase CompanyCore
 * @param coverLetter the parsed cover-letter object (uses companyInfo + signature)
 */
export function buildCompanyIdentity(profile: any, coverLetter?: any): ExportCompanyIdentity {
  const p = profile || {};
  const ci = coverLetter?.companyInfo || {};
  const sig = coverLetter?.signature || {};

  const companyName = firstReal(
    s(p.company_name),
    s(p.companyName),
    s(ci.name),
    s(sig.company)
  );

  // Address: profile object first, else the cover letter's flat lines.
  let addressLines = formatAddress(p.business_address ?? p.businessAddress);
  if (addressLines.length === 0) {
    addressLines = [s(ci.address), s(ci.cityStateZip)].filter(Boolean);
  }

  const websiteRaw = firstReal(s(p.website_url), s(p.websiteUrl), s(ci.website));

  const identity: ExportCompanyIdentity = {
    companyName,
    logoUrl: firstReal(s(p.company_logo_url), s(p.companyLogoUrl)) || null,
    addressLines,
    phone: firstReal(s(p.phone), s(ci.phone)),
    email: firstReal(s(p.email), s(ci.email)),
    website: websiteRaw ? displayWebsite(websiteRaw) : '',
    uei: firstReal(s(p.uei_number), s(p.ueiNumber)),
    cage: firstReal(s(p.cage_code), s(p.cageCode)),
    duns: firstReal(s(p.duns_number), s(p.dunsNumber)),
    pocName: firstReal(s(sig.name), s(p.poc_name), s(p.contact_name)),
    pocTitle: firstReal(s(sig.title), s(p.poc_title)),
    hasIdentity: false,
  };

  identity.hasIdentity = Boolean(
    identity.companyName ||
      identity.phone ||
      identity.email ||
      identity.website ||
      identity.uei ||
      identity.cage ||
      identity.duns ||
      identity.addressLines.length
  );

  return identity;
}
